import {
  UPDATE_SIZE_EVENT,
  ACTION_MANUAL_SCROLL,
  ACTION_BEFORE_MANUAL_SMOOTH_SCROLL,
  ACTION_ITEM_RESIZE,
  ACTION_VIEWPORT_RESIZE,
  type VirtualStore,
} from "./store.js";
import {
  createResizeObserver,
  createScrollObserver,
  normalizeScrollOffset,
  type ScrollObserver,
} from "./observer.js";
import { type ItemResize } from "./types.js";
import {
  cancelTimeout,
  createPromise,
  max,
  microtask,
  NULL,
  timeout,
} from "./utils.js";
import {
  getCurrentDocument,
  getCurrentWindow,
  getDocumentElement,
  isSmoothScrollSupported,
} from "./environment.js";

type ScheduleScrollFunction = (
  getTargetOffset: () => number,
  smooth?: boolean,
) => Promise<void>;

const createScrollScheduler = (
  store: VirtualStore,
  initialized: () => Promise<boolean>,
  scroll: (offset: number, smooth?: boolean) => void,
): [scroll: ScheduleScrollFunction, cancel: () => void] => {
  let cancelScroll: (() => void) | undefined;

  // The given offset will be clamped by browser
  // https://drafts.csswg.org/cssom-view/#dom-element-scrolltop
  return [
    async (getTargetOffset, smooth) => {
      // Wait for element assign. The element may be undefined if scrollRef prop is used and scroll is scheduled on mount.
      // https://github.com/inokawa/virtua/pull/733
      // https://github.com/inokawa/virtua/pull/750
      if (!(await initialized())) {
        return;
      }

      if (cancelScroll) {
        // Cancel waiting scrollTo
        cancelScroll();
      }

      let stopped: boolean | undefined;
      let timerId: ReturnType<typeof timeout> | undefined;
      let unsubscribe: (() => void) | undefined;

      const isSmooth = smooth && isSmoothScrollSupported();

      // Stopping is kept as a state, not delivered as an event, so it can never be missed by a race with measurement
      // https://github.com/inokawa/virtua/issues/715
      const stop = (cancelScroll = () => {
        stopped = true;
        cancelTimeout(timerId);
        unsubscribe && unsubscribe();
      });

      // The scroll destination is not fixed until the items on the way are measured and the timing is not predictable
      const onMeasured = () => {
        if (stopped) {
          return;
        }

        // Resize event may not happen when the window/tab is not visible, or during browser back in Safari.
        // We have to wait for the initial measurement to avoid failing imperative scroll on mount.
        // https://github.com/inokawa/virtua/issues/450
        if (store.$getViewportSize()) {
          // Stop when items around scroll destination completely measured
          cancelTimeout(timerId);
          timerId = timeout(stop, 150);
        }

        if (isSmooth) {
          // Smooth scrolling can be started only once, so wait for all the items on the way to be measured.
          for (let [i, end] = store.$getRange(0); i <= end; i++) {
            if (store.$isUnmeasuredItem(i)) {
              return;
            }
          }
          stop();
        }

        store.$update(ACTION_MANUAL_SCROLL);
        scroll(getTargetOffset(), isSmooth);
      };

      const start = () => {
        if (stopped) {
          return;
        }
        // Batch the measurements in the same task to scroll only once
        let queued: boolean | undefined;
        unsubscribe = store.$subscribe(UPDATE_SIZE_EVENT, () => {
          if (queued) {
            return;
          }
          queued = true;
          microtask(() => {
            queued = false;
            onMeasured();
          });
        });
        onMeasured();
      };

      if (isSmooth) {
        store.$update(ACTION_BEFORE_MANUAL_SMOOTH_SCROLL, getTargetOffset());
        // https://github.com/inokawa/virtua/issues/590
        microtask(start);
      } else {
        start();
      }
    },
    () => {
      cancelScroll && cancelScroll();
    },
  ];
};

/**
 * @internal
 */
export interface Driver {
  $observe(containerElement: HTMLElement, viewport?: HTMLElement): void;
  $dispose(): void;
  $observeItem(el: HTMLElement, index: number): () => void;
  $isNegative(): boolean;
  $scroll(getTargetOffset: () => number, smooth?: boolean): void;
  $effect(): void;
  $getBaseOffset(): number;
  $getScrollbarSize(): number;
}

/**
 * @internal
 */
export type DriverFactory = (
  store: VirtualStore,
  isHorizontal: boolean,
) => Driver;

/**
 * @internal
 */
export const createContainerDriver: DriverFactory = (store, isHorizontal) => {
  let viewportElement: HTMLElement | undefined;
  let scrollObserver: ScrollObserver | undefined;
  let initialized = createPromise<boolean>();
  let isNegative = false;
  const scrollOffsetKey = isHorizontal ? "scrollLeft" : "scrollTop";
  const overflowKey = isHorizontal ? "overflowX" : "overflowY";

  const [scheduleScroll, cancelScroll] = createScrollScheduler(
    store,
    () => initialized[0],
    (offset, smooth) => {
      offset = normalizeScrollOffset(offset, isNegative);

      if (smooth) {
        viewportElement!.scrollTo({
          [isHorizontal ? "left" : "top"]: offset,
          behavior: "smooth",
        });
      } else {
        viewportElement![scrollOffsetKey] = offset;
      }
    },
  );

  const sizeKey = isHorizontal ? "width" : "height";
  const mountedIndexes = new WeakMap<Element, number>();

  const resizeObserver = createResizeObserver((entries) => {
    const resizes: ItemResize[] = [];
    for (const { target, contentRect } of entries) {
      // Skip zero-sized rects that may be observed under `display: none` style
      if (!(target as HTMLElement).offsetParent) continue;

      if (target === viewportElement) {
        store.$update(ACTION_VIEWPORT_RESIZE, contentRect[sizeKey]);
      } else {
        const index = mountedIndexes.get(target);
        if (index != NULL) {
          resizes.push([index, contentRect[sizeKey]]);
        }
      }
    }

    if (resizes.length) {
      store.$update(ACTION_ITEM_RESIZE, resizes);
    }
  });

  return {
    $observe(containerElement, viewport = containerElement.parentElement!) {
      resizeObserver._observe((viewportElement = viewport));

      if (isHorizontal) {
        isNegative = getComputedStyle(viewport).direction === "rtl";
      }

      scrollObserver = createScrollObserver(
        store,
        viewport,
        isHorizontal,
        () => normalizeScrollOffset(viewport[scrollOffsetKey], isNegative),
        (jump, shift, isMomentumScrolling) => {
          // If we update scroll position while touching on iOS, the position will be reverted.
          // However iOS WebKit fires touch events only once at the beginning of momentum scrolling.
          // That means we have no reliable way to confirm still touched or not if user touches more than once during momentum scrolling...
          // This is a hack for the suspectable situations, inspired by https://github.com/prud/ios-overflow-scroll-to-top
          if (isMomentumScrolling) {
            const style = viewport.style;
            const prev = style[overflowKey];
            style[overflowKey] = "hidden";
            timeout(() => {
              style[overflowKey] = prev;
            });
          }

          // Use absolute position not to exceed scrollable bounds
          // https://github.com/inokawa/virtua/discussions/475
          viewport[scrollOffsetKey] = normalizeScrollOffset(
            store.$getScrollOffset() + jump,
            isNegative,
          );
          if (shift) {
            // https://github.com/inokawa/virtua/issues/357
            cancelScroll();
          }
        },
      );

      initialized[1](true);
    },
    $dispose() {
      resizeObserver._dispose();
      scrollObserver && scrollObserver._dispose();
      initialized[1](false);
      // https://github.com/inokawa/virtua/pull/765
      initialized = createPromise();
    },
    $observeItem(el, index) {
      mountedIndexes.set(el, index);
      resizeObserver._observe(el);
      return () => {
        mountedIndexes.delete(el);
        resizeObserver._unobserve(el);
      };
    },
    $isNegative: () => isNegative,
    $scroll: scheduleScroll,
    $effect() {
      scrollObserver && scrollObserver._fixScrollJump();
    },
    $getBaseOffset: store.$getStartSpacerSize,
    $getScrollbarSize: () => 0,
  };
};

/**
 * @internal
 */
export const createWindowDriver: DriverFactory = (store, isHorizontal) => {
  let containerElement: HTMLElement | undefined;
  let scrollObserver: ScrollObserver | undefined;
  let cleanupOnWindowResize: (() => void) | undefined;
  let initialized = createPromise<boolean>();
  let isNegative = false;
  const scrollToKey = isHorizontal ? "left" : "top";

  const [scheduleScroll] = createScrollScheduler(
    store,
    () => initialized[0],
    (offset, smooth) => {
      offset = normalizeScrollOffset(offset, isNegative);

      const window = getCurrentWindow(getCurrentDocument(containerElement!));

      if (smooth) {
        window.scroll({
          [scrollToKey]: offset,
          behavior: "smooth",
        });
      } else {
        window.scroll({
          [scrollToKey]: offset,
        });
      }
    },
  );

  const sizeKey = isHorizontal ? "width" : "height";
  const mountedIndexes = new WeakMap<Element, number>();

  const resizeObserver = createResizeObserver((entries) => {
    const resizes: ItemResize[] = [];
    for (const { target, contentRect } of entries) {
      // Skip zero-sized rects that may be observed under `display: none` style
      if (!(target as HTMLElement).offsetParent) continue;

      const index = mountedIndexes.get(target);
      if (index != NULL) {
        resizes.push([index, contentRect[sizeKey]]);
      }
    }

    if (resizes.length) {
      store.$update(ACTION_ITEM_RESIZE, resizes);
    }
  });

  const calcOffsetToViewport = (
    node: HTMLElement,
    viewport: HTMLElement,
    window: Window,
    isHorizontal: boolean,
    offset: number = 0,
  ): number => {
    // TODO calc offset only when it changes (maybe impossible)
    const offsetKey = isHorizontal ? "offsetLeft" : "offsetTop";
    const offsetSum =
      offset +
      (isHorizontal && isNegative
        ? window.innerWidth - node[offsetKey] - node.offsetWidth
        : node[offsetKey]);

    const parent = node.offsetParent;
    if (node === viewport || !parent) {
      return offsetSum;
    }

    return calcOffsetToViewport(
      parent as HTMLElement,
      viewport,
      window,
      isHorizontal,
      offsetSum,
    );
  };

  return {
    $observe(container) {
      containerElement = container;
      const scrollOffsetKey = isHorizontal ? "scrollX" : "scrollY";

      const document = getCurrentDocument(container);
      const window = getCurrentWindow(document);

      const onWindowResize = () => {
        store.$update(
          ACTION_VIEWPORT_RESIZE,
          window[isHorizontal ? "innerWidth" : "innerHeight"],
        );
      };
      window.addEventListener("resize", onWindowResize);

      // https://github.com/inokawa/virtua/issues/792
      microtask(onWindowResize);

      cleanupOnWindowResize = () => {
        window.removeEventListener("resize", onWindowResize);
      };

      if (isHorizontal) {
        // Detect RTL document
        isNegative =
          getComputedStyle(getDocumentElement(document)).direction === "rtl";
      }

      scrollObserver = createScrollObserver(
        store,
        window,
        isHorizontal,
        () => normalizeScrollOffset(window[scrollOffsetKey], isNegative),
        (jump, shift) => {
          // TODO support case two window scrollers exist in the same view
          if (shift) {
            // Use absolute position not to exceed scrollable bounds
            window.scroll({
              [scrollToKey]: normalizeScrollOffset(
                store.$getScrollOffset() + jump,
                isNegative,
              ),
            });
          } else {
            // Use window.scrollBy here, which causes less layout shift for some reason.
            window.scrollBy({
              [scrollToKey]: normalizeScrollOffset(jump, isNegative),
            });
          }
        },
        () =>
          calcOffsetToViewport(container, document.body, window, isHorizontal),
      );

      initialized[1](true);
    },
    $dispose() {
      cleanupOnWindowResize && cleanupOnWindowResize();
      resizeObserver._dispose();
      scrollObserver && scrollObserver._dispose();
      containerElement = undefined;
      initialized[1](false);
      // https://github.com/inokawa/virtua/pull/765
      initialized = createPromise();
    },
    $observeItem(el, index) {
      mountedIndexes.set(el, index);
      resizeObserver._observe(el);
      return () => {
        mountedIndexes.delete(el);
        resizeObserver._unobserve(el);
      };
    },
    $isNegative: () => isNegative,
    $scroll: scheduleScroll,
    $effect() {
      scrollObserver && scrollObserver._fixScrollJump();
    },
    $getBaseOffset() {
      const document = getCurrentDocument(containerElement!);
      // Calculate target scroll position including container's offset from document
      return calcOffsetToViewport(
        containerElement!,
        document.body,
        getCurrentWindow(document),
        isHorizontal,
      );
    },
    $getScrollbarSize() {
      const html = getDocumentElement(getCurrentDocument(containerElement!));
      return (
        store.$getViewportSize() -
        (isHorizontal ? html.clientWidth : html.clientHeight)
      );
    },
  };
};

/**
 * @internal
 */
export type GridDriver = {
  $observe(containerElement: HTMLElement, viewport?: HTMLElement): void;
  $dispose(): void;
  $observeItem(el: HTMLElement, rowIndex: number, colIndex: number): () => void;
  $resizeRows(rows: ItemResize[]): void;
  $resizeCols(cols: ItemResize[]): void;
  $isNegative(): boolean;
  $scrollX(getTargetOffset: () => number, smooth?: boolean): void;
  $scrollY(getTargetOffset: () => number, smooth?: boolean): void;
  $effect(): void;
};

/**
 * @internal
 */
export type GridDriverFactory = (
  rowStore: VirtualStore,
  colStore: VirtualStore,
) => GridDriver;

/**
 * @internal
 */
export const createContainerGridDriver: GridDriverFactory = (
  rowStore,
  colStore,
): GridDriver => {
  let viewportElement: HTMLElement | undefined;
  let rowScrollObserver: ScrollObserver | undefined;
  let colScrollObserver: ScrollObserver | undefined;
  let initialized = createPromise<boolean>();
  let isNegative = false;

  const [scheduleScrollX, cancelScrollX] = createScrollScheduler(
    colStore,
    () => initialized[0],
    (offset, smooth) => {
      const opts: ScrollToOptions = {
        left: normalizeScrollOffset(offset, isNegative),
      };
      if (smooth) {
        opts.behavior = "smooth";
      }
      viewportElement!.scrollTo(opts);
    },
  );
  const [scheduleScrollY, cancelScrollY] = createScrollScheduler(
    rowStore,
    () => initialized[0],
    (offset, smooth) => {
      const opts: ScrollToOptions = { top: offset };
      if (smooth) {
        opts.behavior = "smooth";
      }
      viewportElement!.scrollTo(opts);
    },
  );

  const mountedIndexes = new WeakMap<
    Element,
    [rowIndex: number, colIndex: number]
  >();

  type CellSize = [height: number, width: number];
  const maybeCachedRowIndexes = new Set<number>();
  const maybeCachedColIndexes = new Set<number>();
  const sizeCache = new Map<string, CellSize>();
  const getKey = (rowIndex: number, colIndex: number): string =>
    `${rowIndex}-${colIndex}`;

  const resizeObserver = createResizeObserver((entries) => {
    const resizedRows = new Set<number>();
    const resizedCols = new Set<number>();
    for (const {
      target,
      contentRect: { width, height },
    } of entries) {
      // Skip zero-sized rects that may be observed under `display: none` style
      if (!(target as HTMLElement).offsetParent) continue;

      if (target === viewportElement) {
        rowStore.$update(ACTION_VIEWPORT_RESIZE, height);
        colStore.$update(ACTION_VIEWPORT_RESIZE, width);
      } else {
        const cell = mountedIndexes.get(target);
        if (cell) {
          const [rowIndex, colIndex] = cell;
          const key = getKey(rowIndex, colIndex);
          const prevSize = sizeCache.get(key);
          let rowResized: boolean | undefined;
          let colResized: boolean | undefined;
          if (!prevSize) {
            rowResized = colResized = true;
          } else {
            if (prevSize[0] !== height) {
              rowResized = true;
            }
            if (prevSize[1] !== width) {
              colResized = true;
            }
          }
          if (rowResized) {
            resizedRows.add(rowIndex);
          }
          if (colResized) {
            resizedCols.add(colIndex);
          }
          if (rowResized || colResized) {
            sizeCache.set(key, [height, width]);
          }
        }
      }
    }

    if (resizedRows.size) {
      const heightResizes: ItemResize[] = [];
      resizedRows.forEach((rowIndex) => {
        let maxHeight = 0;
        maybeCachedColIndexes.forEach((colIndex) => {
          const size = sizeCache.get(getKey(rowIndex, colIndex));
          if (size) {
            maxHeight = max(maxHeight, size[0]);
          }
        });
        if (maxHeight) {
          heightResizes.push([rowIndex, maxHeight]);
        }
      });
      rowStore.$update(ACTION_ITEM_RESIZE, heightResizes);
    }
    if (resizedCols.size) {
      const widthResizes: ItemResize[] = [];
      resizedCols.forEach((colIndex) => {
        let maxWidth = 0;
        maybeCachedRowIndexes.forEach((rowIndex) => {
          const size = sizeCache.get(getKey(rowIndex, colIndex));
          if (size) {
            maxWidth = max(maxWidth, size[1]);
          }
        });
        if (maxWidth) {
          widthResizes.push([colIndex, maxWidth]);
        }
      });
      colStore.$update(ACTION_ITEM_RESIZE, widthResizes);
    }
  });

  const observeAxisScroll = (
    store: VirtualStore,
    isHorizontal: boolean,
    cancelScroll: () => void,
  ) => {
    const viewport = viewportElement!;
    const scrollOffsetKey = isHorizontal ? "scrollLeft" : "scrollTop";
    const overflowKey = isHorizontal ? "overflowX" : "overflowY";
    const negative = isHorizontal && isNegative;

    return createScrollObserver(
      store,
      viewport,
      isHorizontal,
      () => normalizeScrollOffset(viewport[scrollOffsetKey], negative),
      (jump, shift, isMomentumScrolling) => {
        // If we update scroll position while touching on iOS, the position will be reverted.
        // However iOS WebKit fires touch events only once at the beginning of momentum scrolling.
        // That means we have no reliable way to confirm still touched or not if user touches more than once during momentum scrolling...
        // This is a hack for the suspectable situations, inspired by https://github.com/prud/ios-overflow-scroll-to-top
        if (isMomentumScrolling) {
          const style = viewport.style;
          const prev = style[overflowKey];
          style[overflowKey] = "hidden";
          timeout(() => {
            style[overflowKey] = prev;
          });
        }

        // Use absolute position not to exceed scrollable bounds
        // https://github.com/inokawa/virtua/discussions/475
        viewport[scrollOffsetKey] = normalizeScrollOffset(
          store.$getScrollOffset() + jump,
          negative,
        );
        if (shift) {
          // https://github.com/inokawa/virtua/issues/357
          cancelScroll();
        }
      },
    );
  };

  return {
    $observe(containerElement, viewport = containerElement.parentElement!) {
      resizeObserver._observe((viewportElement = viewport));

      // Detect RTL document
      isNegative = getComputedStyle(viewport).direction === "rtl";

      rowScrollObserver = observeAxisScroll(rowStore, false, cancelScrollY);
      colScrollObserver = observeAxisScroll(colStore, true, cancelScrollX);

      initialized[1](true);
    },
    $dispose() {
      resizeObserver._dispose();
      rowScrollObserver && rowScrollObserver._dispose();
      colScrollObserver && colScrollObserver._dispose();
      initialized[1](false);
      // https://github.com/inokawa/virtua/pull/765
      initialized = createPromise();
    },
    $observeItem(el, rowIndex, colIndex) {
      mountedIndexes.set(el, [rowIndex, colIndex]);
      maybeCachedRowIndexes.add(rowIndex);
      maybeCachedColIndexes.add(colIndex);
      resizeObserver._observe(el);
      return () => {
        mountedIndexes.delete(el);
        resizeObserver._unobserve(el);
      };
    },
    $resizeRows(rows) {
      for (const [r] of rows) {
        for (let c = 0; c < colStore.$getItemsLength(); c++) {
          sizeCache.delete(getKey(r, c));
        }
      }
      rowStore.$update(ACTION_ITEM_RESIZE, rows);
    },
    $resizeCols(cols) {
      for (const [c] of cols) {
        for (let r = 0; r < rowStore.$getItemsLength(); r++) {
          sizeCache.delete(getKey(r, c));
        }
      }
      colStore.$update(ACTION_ITEM_RESIZE, cols);
    },
    $isNegative: () => isNegative,
    $scrollX: scheduleScrollX,
    $scrollY: scheduleScrollY,
    $effect() {
      rowScrollObserver && rowScrollObserver._fixScrollJump();
      colScrollObserver && colScrollObserver._fixScrollJump();
    },
  };
};
